export const PCE_SERIAL_NUMBER_PATTERN = /^\d{3}$/
export const MIN_NAME_LETTERS = 3
export const GRDF_UNAVAILABLE_BLOCK_SECONDS = 180
export const OCTOPUS_LIMIT_REACHED_BLOCK_SECONDS = 60

export const OCTOPUS_NO_METER_POINT = 'KT-FR-4530'
export const OCTOPUS_SEVERAL_METER_POINTS = 'KT-FR-4531'
export const OCTOPUS_GRDF_UNAVAILABLE = 'KT-FR-4532'
export const OCTOPUS_LIMIT_REACHED = 'KT-CT-1199'
// Kept in sync with PceSearchRules::FINAL_ERROR_CODES in multi-provider-platform.
const OCTOPUS_OTHER_FINAL_ERRORS = ['KT-CT-4410', 'KT-CT-1601', 'KT-CT-1605']

export const PCE_SEARCH_GRDF_NOT_COVERING = 'PCE_SEARCH_GRDF_NOT_COVERING'
export const PCE_SEARCH_GRDF_UNAVAILABLE = 'PCE_SEARCH_GRDF_UNAVAILABLE'
export const PCE_SEARCH_RATE_LIMITED = 'PCE_SEARCH_RATE_LIMITED'

export type PceSearchFailureReason = 'grdf_not_covering' | 'several_meters' | 'no_meter' | 'already_failed'
export type PceSearchBlockReason = 'rate_limited' | 'grdf_unavailable'

export interface PceSearchParams {
  inseeCode: string
  streetNumber: string
  streetName: string
  occupant: string
  serialNumber: string
}

export interface PceFailedSearch {
  reason: PceSearchFailureReason
  params: PceSearchParams | null
}

const normalize = (value: string | null | undefined): string => (value ?? '').trim().toUpperCase()

const sameValue = (left: string, right: string): boolean => normalize(left) === normalize(right)

const sameAddress = (left: PceSearchParams, right: PceSearchParams): boolean =>
  sameValue(left.inseeCode, right.inseeCode) &&
  sameValue(left.streetNumber, right.streetNumber) &&
  sameValue(left.streetName, right.streetName)

const sameSearch = (left: PceSearchParams, right: PceSearchParams): boolean =>
  sameAddress(left, right) &&
  sameValue(left.occupant, right.occupant) &&
  sameValue(left.serialNumber, right.serialNumber)

export const streetNumberDigits = (streetNumber: string | null | undefined): string =>
  (streetNumber ?? '').match(/\d+/)?.[0] ?? ''

export const countLetters = (value: string): number => (value.match(/\p{L}/gu) ?? []).length

export const isValidSerialNumber = (serialNumber: string): boolean =>
  PCE_SERIAL_NUMBER_PATTERN.test(serialNumber.trim())

export const isSerialNumberInvalid = (serialNumber: string): boolean =>
  serialNumber.trim() !== '' && !isValidSerialNumber(serialNumber)

export const hasEnoughNameLetters = (occupant: string): boolean => countLetters(occupant) >= MIN_NAME_LETTERS

export const canSearchWithName = ({ occupant, serialNumber }: { occupant: string; serialNumber: string }): boolean =>
  isValidSerialNumber(serialNumber) || hasEnoughNameLetters(occupant)

export const searchedParams = <T extends { occupant: string; serialNumber: string }>(params: T): T =>
  isValidSerialNumber(params.serialNumber ?? '') ? { ...params, occupant: '' } : params

export const failureReasonOf = (octopusErrorCode: string | null | undefined): PceSearchFailureReason | null => {
  if (octopusErrorCode === OCTOPUS_SEVERAL_METER_POINTS) return 'several_meters'
  if (octopusErrorCode === OCTOPUS_NO_METER_POINT) return 'no_meter'
  if (OCTOPUS_OTHER_FINAL_ERRORS.includes(octopusErrorCode ?? '')) return 'already_failed'

  return null
}

export const blockedFailureReason = (
  typedParams: PceSearchParams,
  lastFailedSearch: PceFailedSearch | null,
): PceSearchFailureReason | null => {
  if (!lastFailedSearch?.params) return null

  const params = searchedParams(typedParams)
  const failedParams = searchedParams(lastFailedSearch.params)

  switch (lastFailedSearch.reason) {
    case 'grdf_not_covering':
      return sameValue(params.inseeCode, failedParams.inseeCode) ? 'grdf_not_covering' : null
    case 'several_meters':
      return sameAddress(params, failedParams) && sameValue(params.serialNumber, failedParams.serialNumber)
        ? 'several_meters'
        : null
    case 'no_meter':
      if (normalize(failedParams.occupant) === '') return sameSearch(params, failedParams) ? 'already_failed' : null

      return sameAddress(params, failedParams) && sameValue(params.occupant, failedParams.occupant) ? 'no_meter' : null
    default:
      return sameSearch(params, failedParams) ? 'already_failed' : null
  }
}
